import { type FormEvent, useState } from "react";

import { Button } from "../../core/Button.js";
import { Modal, ModalClose, ModalFooter } from "../../core/Modal.js";
import { RadioGroup, type RadioOption } from "../../core/RadioGroup.js";
import { Text } from "../../core/Text.js";
import { Textarea } from "../../core/Textarea.js";

export interface ChatReportModalProps<Reason extends string> {
  /** Open while a message is being reported. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Report message". */
  title: string;
  /** Whose message, and what happens next: "From mira. Staff will review it." */
  description?: string;
  /** The question the reasons answer: "Reason". */
  reasonLabel: string;
  reasons: readonly RadioOption<Reason>[];
  /** "Details (optional)". */
  noteLabel: string;
  noteMaxLength?: number;
  submitLabel: string;
  cancelLabel: string;
  /** Sends the report. The modal keeps what was entered until it closes. */
  onSubmit: (report: { reason: Reason; note: string }) => void;
  /** The report is on its way: the send button shows it. */
  submitting?: boolean;
  /** Why the last send failed, in words. */
  error?: string;
  /** Shown in place of the form once the report has gone: "Thanks. The report was sent to staff." */
  sent?: { message: string; doneLabel: string };
}

/**
 * Reports one chat message to the store's staff: a reason, an optional note, then a
 * confirmation in place of the form. Sending is the caller's; the modal holds the draft.
 */
export function ChatReportModal<Reason extends string>({
  open,
  onOpenChange,
  title,
  description,
  reasonLabel,
  reasons,
  noteLabel,
  noteMaxLength,
  submitLabel,
  cancelLabel,
  onSubmit,
  submitting = false,
  error,
  sent,
}: ChatReportModalProps<Reason>) {
  const [reason, setReason] = useState<Reason | undefined>(undefined);
  const [note, setNote] = useState("");
  const change = (next: boolean) => {
    // A closed report starts over the next time it opens.
    if (!next) {
      setReason(undefined);
      setNote("");
    }
    onOpenChange(next);
  };
  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!reason || submitting) return;
    onSubmit({ reason, note: note.trim() });
  };
  return (
    <Modal
      open={open}
      onOpenChange={change}
      title={title}
      description={description}
      size="sm"
      footer={
        sent ? (
          <ModalFooter>
            <ModalClose render={<Button />}>{sent.doneLabel}</ModalClose>
          </ModalFooter>
        ) : (
          <ModalFooter>
            <ModalClose render={<Button variant="ghost" />}>{cancelLabel}</ModalClose>
            {/* The footer sits outside the form, so the button sends it itself. */}
            <Button onClick={() => submit()} disabled={!reason} loading={submitting}>
              {submitLabel}
            </Button>
          </ModalFooter>
        )
      }
    >
      {sent ? (
        <Text variant="body-b2" tone="primary" role="status">
          {sent.message}
        </Text>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <RadioGroup
            label={reasonLabel}
            options={reasons}
            value={reason}
            onValueChange={setReason}
          />
          <Textarea
            label={noteLabel}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={noteMaxLength}
            showCount={noteMaxLength !== undefined}
            rows={3}
            error={error}
          />
        </form>
      )}
    </Modal>
  );
}
